import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Plus, Download, Trash2 } from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Using centralized api

function Documents() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_url: '',
    category: ''
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/documents', formData);
      toast.success('Documento agregado');
      setDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        file_url: '',
        category: ''
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error creating document:', error);
      toast.error('Error al agregar documento');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;
    
    try {
      await api.delete(`/documents/${documentId}`);
      toast.success('Documento eliminado');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error al eliminar documento');
    }
  };

  const groupDocumentsByCategory = () => {
    const grouped = {};
    documents.forEach(doc => {
      if (!grouped[doc.category]) grouped[doc.category] = [];
      grouped[doc.category].push(doc);
    });
    return grouped;
  };

  const groupedDocuments = groupDocumentsByCategory();

  const categoryColors = {
    'Contrato': 'bg-blue-100 text-blue-700 border-blue-200',
    'Médico': 'bg-red-100 text-red-700 border-red-200',
    'Legal': 'bg-purple-100 text-purple-700 border-purple-200',
    'Estadísticas': 'bg-green-100 text-green-700 border-green-200',
    'Otro': 'bg-gray-100 text-gray-700 border-gray-200'
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="documents-page">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Documentos</h1>
            <p className="text-muted-foreground">Almacena y organiza documentos importantes</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-document-button">
                <Plus className="h-4 w-4 mr-2" />
                Agregar Documento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Agregar Documento</DialogTitle>
                <DialogDescription>Sube un nuevo documento al sistema</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    data-testid="document-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="category">Categoría *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger data-testid="document-category-select">
                      <SelectValue placeholder="Selecciona categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Contrato">Contrato</SelectItem>
                      <SelectItem value="Médico">Médico</SelectItem>
                      <SelectItem value="Legal">Legal</SelectItem>
                      <SelectItem value="Estadísticas">Estadísticas</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="file_url">URL del Archivo *</Label>
                  <Input
                    id="file_url"
                    data-testid="document-url-input"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://ejemplo.com/documento.pdf"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="submit-document-button">Agregar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {documents.length === 0 ? (
          <Card className="bento-card">
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No hay documentos guardados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedDocuments).map(([category, categoryDocs]) => (
              <div key={category}>
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <span className={`px-3 py-1 rounded-full text-sm ${categoryColors[category]}`}>
                    {category}
                  </span>
                  <span className="ml-2 text-muted-foreground text-sm font-normal">
                    ({categoryDocs.length})
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryDocs.map((doc) => (
                    <Card key={doc.document_id} className="bento-card group">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="p-3 rounded-lg bg-secondary">
                            <FileText className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex space-x-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => window.open(doc.file_url, '_blank')}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {user?.role === 'agent' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(doc.document_id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <h3 className="font-semibold mb-1 line-clamp-2">{doc.title}</h3>
                        {doc.description && (
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {doc.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{doc.uploaded_by_name}</span>
                          <span>
                            {new Date(doc.uploaded_at).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Documents;